import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  FormControlLabel,
  Checkbox,
  IconButton,
  Divider,
  Tooltip,
  Slider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/Remove';
import ChatIcon from '@mui/icons-material/Chat';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { getDashboardConfig, updateDashboardConfig, createDashboardConfig, getAppAnalytics, listAppShares, addAppShare, removeAppShare } from '../api/dashboardConfig.api';
import axiosClient from '../api/axios.client';
import { getUserFlows } from '../api/agenticFlow.api';
import { COMPONENT_TYPES, OUTPUT_KEYS, COMPONENT_REGISTRY, getDefaultOutputKey } from '../components/Dashboard/controlRegistry';
import { DashboardProvider } from '../contexts/DashboardContext';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import AppBuilderComponentPalette from '../components/Dashboard/AppBuilderComponentPalette';
import AppBuilderAIAssistant from '../components/Dashboard/AppBuilderAIAssistant';
import DashboardChatBar from '../components/Dashboard/DashboardChatBar';
import AppThemeProvider, { THEMES } from '../components/Dashboard/AppThemeProvider';
import SettingsIcon from '@mui/icons-material/Settings';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletIcon from '@mui/icons-material/Tablet';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';

function extractFlows(flowRes) {
  let flowList = [];
  if (flowRes?.err) return flowList;
  const response = flowRes?.response;
  if (response?.flows && Array.isArray(response.flows)) flowList = response.flows;
  else if (Array.isArray(response)) flowList = response;
  else if (response?.data?.flows) flowList = response.data.flows;
  else if (flowRes?.flows) flowList = flowRes.flows;
  return flowList;
}

export default function AppBuilderEditPage({ darkMode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    defaultFlowId: '',
    layout: 'three-panel',
    chatPosition: 'center',
    theme: {
      themeId: 'ocean',
      mode: 'auto',
      headerStyle: 'flat',
      fontFamily: '',
      borderRadius: 8,
      logoUrl: '',
    },
  });
  const [panelForm, setPanelForm] = useState(null);
  const [drawerContent, setDrawerContent] = useState(null); // 'settings' | 'panel' | null
  const [addPanelDialogOpen, setAddPanelDialogOpen] = useState(false);
  const [addPanelForm, setAddPanelForm] = useState({ position: 'left', componentType: 'table', outputKey: 'table_data', flowId: '', chartType: 'bar', refreshOnMount: false });
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [previewSize, setPreviewSize] = useState('desktop'); // 'desktop' | 'tablet' | 'phone'
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [shares, setShares] = useState([]);
  const [shareSearchQuery, setShareSearchQuery] = useState('');
  const [shareSearchResults, setShareSearchResults] = useState([]);
  const [shareRoleToAdd, setShareRoleToAdd] = useState('editor');
  const [addingShare, setAddingShare] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) {
        navigate('/dashboard-builder', { replace: true });
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [configRes, flowRes] = await Promise.all([
          getDashboardConfig(id),
          getUserFlows({}),
        ]);
        if (!mounted) return;
        const { data, error: err } = configRes;
        if (err || !data) {
          setError(err || 'App not found');
          setApp(null);
          setLoading(false);
          return;
        }
        setApp(data);
        const th = data.theme || {};
        setForm({
          name: data.name || '',
          description: data.description || '',
          defaultFlowId: data.defaultFlowId || '',
          layout: data.layout || 'three-panel',
          chatPosition: data.chatPosition || 'center',
          theme: {
            themeId: THEMES[th.themeId || th.colorPreset] ? (th.themeId || th.colorPreset) : 'ocean',
            mode: th.mode ?? 'auto',
            headerStyle: th.headerStyle || 'flat',
            fontFamily: th.fontFamily || '',
            borderRadius: typeof th.borderRadius === 'number' ? th.borderRadius : 8,
            logoUrl: th.logoUrl || '',
          },
        });
        setFlows(extractFlows(flowRes));
      } catch (e) {
        if (mounted) setError(e?.message || 'Failed to load');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id, navigate]);

  useEffect(() => {
    if (!app?._id && !app?.id) return;
    getAppAnalytics(app._id || app.id).then(({ data }) => setAnalytics(data));
  }, [app?._id, app?.id]);

  useEffect(() => {
    if (drawerContent !== 'settings' || !app?._id && !app?.id) return;
    listAppShares(app._id || app.id).then(({ data }) => setShares(data || []));
  }, [drawerContent, app?._id, app?.id]);

  useEffect(() => {
    if (!shareSearchQuery || shareSearchQuery.length < 2) {
      setShareSearchResults([]);
      return;
    }
    const t = setTimeout(() => {
      axiosClient.get(`users/search?q=${encodeURIComponent(shareSearchQuery)}`).then((res) => {
        setShareSearchResults(res?.users || []);
      }).catch(() => setShareSearchResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [shareSearchQuery]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!app) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handlersRef.current.handleSaveApp();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        navigate(`/dashboard-builder/run/${app._id || app.id}`);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handlersRef.current.handleRedo();
        else handlersRef.current.handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [app, navigate]);

  const handleExportApp = () => {
    if (!app) return;
    const exportData = {
      name: app.name,
      description: app.description,
      defaultFlowId: app.defaultFlowId,
      layout: app.layout,
      chatPosition: app.chatPosition,
      theme: app.theme,
      panels: app.panels,
      gridLayout: app.gridLayout,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(app.name || 'app').replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportApp = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const payload = {
        name: data.name ? `${data.name} (Imported)` : 'Imported App',
        description: data.description || '',
        defaultFlowId: data.defaultFlowId || null,
        layout: data.layout || 'three-panel',
        chatPosition: data.chatPosition || 'center',
        theme: data.theme || {},
        panels: data.panels || [],
        gridLayout: data.gridLayout || null,
      };
      const { data: created, error: err } = await createDashboardConfig(payload);
      if (err) setError(err);
      else if (created) navigate(`/dashboard-builder/edit/${created._id || created.id}`);
    } catch (err) {
      setError(err?.message || 'Invalid JSON file');
    }
    e.target.value = '';
  };

  const loadFlows = async () => {
    setFlowsLoading(true);
    const flowRes = await getUserFlows({});
    setFlows(extractFlows(flowRes));
    setFlowsLoading(false);
  };

  const handleSaveApp = async () => {
    if (!app?._id && !app?.id) return;
    setSaving(true);
    setError(null);
    const versions = app?.versions || [];
    const newVersion = {
      id: `v_${Date.now()}`,
      timestamp: new Date().toISOString(),
      name: form.name || 'Untitled App',
      description: form.description || '',
      defaultFlowId: form.defaultFlowId || null,
      layout: form.layout || 'three-panel',
      chatPosition: form.chatPosition || 'center',
      theme: form.theme || null,
      panels: JSON.parse(JSON.stringify(app.panels || [])),
    };
    const updatedVersions = [newVersion, ...versions].slice(0, 20);
    const themeToSave = form.theme && typeof form.theme === 'object' ? form.theme : {};
    const payload = {
      name: form.name || 'Untitled App',
      description: form.description || '',
      defaultFlowId: form.defaultFlowId || null,
      layout: form.layout || 'three-panel',
      chatPosition: form.chatPosition || 'center',
      theme: themeToSave,
      versions: updatedVersions,
    };
    const { data: saved, error: err } = await updateDashboardConfig(app._id || app.id, payload);
    setSaving(false);
    if (err) {
      setError(typeof err === 'string' ? err : err?.message || 'Save failed');
      return;
    }
    if (saved) {
      setApp(saved);
      setForm((prev) => ({
        ...prev,
        name: saved.name ?? prev.name,
        description: saved.description ?? prev.description,
        defaultFlowId: saved.defaultFlowId ?? prev.defaultFlowId,
        layout: saved.layout ?? prev.layout,
        chatPosition: saved.chatPosition ?? prev.chatPosition,
        theme: saved.theme && typeof saved.theme === 'object' ? saved.theme : prev.theme,
      }));
    } else {
      setApp((prev) => ({ ...prev, ...form, versions: updatedVersions }));
    }
  };

  const handleRestoreVersion = async (version) => {
    if (!app?._id || !version) return;
    if (!window.confirm(`Restore to version from ${new Date(version.timestamp).toLocaleString()}?`)) return;
    setSaving(true);
    setError(null);
    const payload = {
      name: version.name,
      description: version.description || '',
      defaultFlowId: version.defaultFlowId || null,
      layout: version.layout || 'three-panel',
      chatPosition: version.chatPosition || 'center',
      theme: version.theme || null,
      panels: version.panels || [],
    };
    const { error: err } = await updateDashboardConfig(app._id || app.id, payload);
    setSaving(false);
    if (err) setError(err);
    else {
      setForm({
        name: version.name || '',
        description: version.description || '',
        defaultFlowId: version.defaultFlowId || '',
        layout: version.layout || 'three-panel',
        chatPosition: version.chatPosition || 'center',
        theme: version.theme || { primaryColor: '', logoUrl: '' },
      });
      setApp((prev) => ({ ...prev, ...payload }));
    }
  };

  const openPanelConfig = (panel) => {
    const ct = panel.componentType || 'table';
    setPanelForm({
      panelId: panel.id,
      panelPosition: panel.position,
      panelLabel: COMPONENT_REGISTRY[panel.componentType]?.label || panel.componentType || 'Panel',
      componentType: ct,
      flowId: panel.flowId || '',
      outputKey: panel.outputKey || getDefaultOutputKey(ct),
      chartType: panel.config?.chartType || 'bar',
      refreshOnMount: panel.config?.refreshOnMount === true,
      collapsible: panel.config?.collapsible === true,
      collapsedDefault: panel.config?.collapsedDefault === true,
      visibilityCondition: panel.config?.visibilityCondition || '',
      drillDownFlowId: panel.config?.drillDownFlowId || '',
      drillDownTargetPanelId: panel.config?.drillDownTargetPanelId || '',
    });
    setDrawerContent('panel');
    if (flows.length === 0) loadFlows();
  };

  const pushUndo = () => {
    setUndoStack((s) => [...s.slice(-49), JSON.parse(JSON.stringify(app.panels || []))]);
    setRedoStack([]);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((s) => [...s, JSON.parse(JSON.stringify(app.panels || []))]);
    setUndoStack((s) => s.slice(0, -1));
    setApp((a) => ({ ...a, panels: prev }));
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((s) => [...s, JSON.parse(JSON.stringify(app.panels || []))]);
    setRedoStack((s) => s.slice(0, -1));
    setApp((a) => ({ ...a, panels: next }));
  };

  const handlersRef = React.useRef({ handleSaveApp, handleUndo, handleRedo });
  handlersRef.current = { handleSaveApp, handleUndo, handleRedo };

  const savePanelConfig = async () => {
    if (!panelForm?.panelId || !app?.panels) {
      setDrawerContent(null);
      return;
    }
    pushUndo();
    const panels = app.panels.map((p) => {
      if (p.id !== panelForm.panelId || p.type === 'chat') return p;
      return {
        ...p,
        componentType: panelForm.componentType,
        flowId: panelForm.flowId || null,
        outputKey: panelForm.outputKey || null,
        config: {
          ...(p.config || {}),
          chartType: panelForm.componentType === 'chart' ? (panelForm.chartType || 'bar') : undefined,
          refreshOnMount: panelForm.refreshOnMount === true,
          collapsible: panelForm.collapsible === true,
          collapsedDefault: panelForm.collapsedDefault === true,
          visibilityCondition: panelForm.visibilityCondition || undefined,
          drillDownFlowId: panelForm.drillDownFlowId || undefined,
          drillDownTargetPanelId: panelForm.drillDownTargetPanelId || undefined,
        },
      };
    });
    const { error: err } = await updateDashboardConfig(app._id || app.id, { panels, layout: 'dynamic' });
    if (err) setError(err);
    else setApp((prev) => ({ ...prev, panels }));
    setDrawerContent(null);
    setPanelForm(null);
  };

  const handleAddPanel = (position = 'left') => {
    setAddPanelForm({ position, componentType: 'table', outputKey: 'table_data', flowId: '', chartType: 'bar', refreshOnMount: false });
    setAddPanelDialogOpen(true);
    if (flows.length === 0) loadFlows();
  };

  const handleDropFromPalette = useCallback(async (componentType, position) => {
    if (!app) return;
    setUndoStack((s) => [...s.slice(-49), JSON.parse(JSON.stringify(app.panels || []))]);
    setRedoStack([]);
    const existingPanels = app.panels || [];
    const positionPanels = existingPanels.filter((p) => p.position === position);
    const isSummary = componentType === 'summary';
    const newPanel = isSummary
      ? { id: `${position}_${Date.now()}`, type: 'summary', position, order: positionPanels.length }
      : {
          id: `${position}_${Date.now()}`,
          type: 'component',
          componentType,
          flowId: null,
          outputKey: getDefaultOutputKey(componentType),
          config: componentType === 'chart' ? { chartType: 'bar' } : {},
          position,
          order: positionPanels.length,
        };
    const panels = [...existingPanels, newPanel];
    const { error: err } = await updateDashboardConfig(app._id || app.id, { panels, layout: 'dynamic' });
    if (err) setError(err);
    else setApp((prev) => ({ ...prev, panels }));
  }, [app, pushUndo]);

  const handlePaletteClickAdd = (componentType) => {
    setAddPanelForm({
      position: 'left',
      componentType,
      outputKey: componentType === 'summary' ? null : getDefaultOutputKey(componentType),
      flowId: '',
      chartType: 'bar',
      refreshOnMount: false,
    });
    setAddPanelDialogOpen(true);
    if (flows.length === 0) loadFlows();
  };

  const handleAIAssistantApply = async (suggestion) => {
    if (!app || !suggestion) return;
    pushUndo();
    const existingPanels = app.panels || [];
    const hasChat = existingPanels.some((p) => p.type === 'chat');
    const newPanels = (suggestion.panels || []).map((p, i) => {
      const posCount = existingPanels.filter((x) => x.position === p.position).length + i;
      if (p.type === 'chat') {
        return hasChat ? null : { id: `center_${Date.now()}`, type: 'chat', position: 'center', order: 0 };
      }
      return {
        id: `${p.position}_${Date.now()}_${i}`,
        type: p.type === 'summary' ? 'summary' : 'component',
        componentType: p.type === 'summary' ? null : p.type,
        flowId: null,
        outputKey: p.type === 'summary' ? null : getDefaultOutputKey(p.type),
        config: p.type === 'chart' ? { chartType: 'bar' } : {},
        position: p.position,
        order: posCount,
      };
    }).filter(Boolean);
    const panels = [...existingPanels, ...newPanels];
    const { error: err } = await updateDashboardConfig(app._id || app.id, {
      panels,
      layout: suggestion.layout || form.layout,
    });
    if (err) setError(err);
    else {
      setApp((prev) => ({ ...prev, panels }));
      setForm((f) => ({ ...f, layout: suggestion.layout || f.layout }));
    }
  };

  const saveAddPanel = async () => {
    if (!app) {
      setAddPanelDialogOpen(false);
      return;
    }
    pushUndo();
    const existingPanels = app.panels || [];
    const positionPanels = existingPanels.filter((p) => p.position === addPanelForm.position);
    const isSummary = addPanelForm.componentType === 'summary';
    const newPanel = isSummary
      ? { id: `${addPanelForm.position}_${Date.now()}`, type: 'summary', position: addPanelForm.position, order: positionPanels.length }
      : {
          id: `${addPanelForm.position}_${Date.now()}`,
          type: 'component',
          componentType: addPanelForm.componentType,
          flowId: addPanelForm.flowId || null,
          outputKey: addPanelForm.outputKey,
          config: {
            ...(addPanelForm.componentType === 'chart' ? { chartType: addPanelForm.chartType || 'bar' } : {}),
            refreshOnMount: addPanelForm.refreshOnMount === true,
          },
          position: addPanelForm.position,
          order: positionPanels.length,
        };
    const panels = [...existingPanels, newPanel];
    const { error: err } = await updateDashboardConfig(app._id || app.id, { panels, layout: 'dynamic' });
    if (err) setError(err);
    else setApp((prev) => ({ ...prev, panels }));
    setAddPanelDialogOpen(false);
  };

  const handleRemovePanel = async (panelId) => {
    if (!window.confirm('Remove this panel?')) return;
    pushUndo();
    const panel = app.panels.find((p) => p.id === panelId);
    if (panel?.type === 'chat') {
      setError('Cannot remove the chat panel');
      return;
    }
    const panels = app.panels.filter((p) => p.id !== panelId);
    const { error: err } = await updateDashboardConfig(app._id || app.id, { panels, layout: panels.length > 3 ? 'dynamic' : 'three-panel' });
    if (err) setError(err);
    else setApp((prev) => ({ ...prev, panels }));
  };

  if (loading || !app) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        {error ? (
          <Alert severity="error" action={<Button onClick={() => navigate('/dashboard-builder')}>Back</Button>}>
            {error}
          </Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/dashboard-builder')}>
          List
        </Button>
        <Button startIcon={<EditIcon />} variant="contained" size="small" color="secondary" disabled>
          Edit
        </Button>
        <Button
          startIcon={<PlayArrowIcon />}
          variant="outlined"
          size="small"
          onClick={() => navigate(`/dashboard-builder/run/${app._id || app.id}`)}
        >
          Play
        </Button>
        <Button
          startIcon={<SettingsIcon />}
          size="small"
          variant={drawerContent === 'settings' ? 'contained' : 'outlined'}
          onClick={() => setDrawerContent((c) => (c === 'settings' ? null : 'settings'))}
        >
          Settings
        </Button>
        <Tooltip title="Undo">
          <span>
            <IconButton size="small" onClick={handleUndo} disabled={undoStack.length === 0}>
              <UndoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo">
          <span>
            <IconButton size="small" onClick={handleRedo} disabled={redoStack.length === 0}>
              <RedoIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Phone preview">
          <IconButton size="small" color={previewSize === 'phone' ? 'primary' : 'default'} onClick={() => setPreviewSize('phone')}>
            <PhoneIphoneIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Tablet preview">
          <IconButton size="small" color={previewSize === 'tablet' ? 'primary' : 'default'} onClick={() => setPreviewSize('tablet')}>
            <TabletIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Desktop preview">
          <IconButton size="small" color={previewSize === 'desktop' ? 'primary' : 'default'} onClick={() => setPreviewSize('desktop')}>
            <DesktopWindowsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export app (JSON)">
          <IconButton size="small" onClick={handleExportApp}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Import app from JSON">
          <IconButton size="small" component="label">
            <input ref={fileInputRef} type="file" accept=".json" hidden onChange={handleImportApp} />
            <UploadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="AI Assistant (describe your app)">
          <IconButton size="small" onClick={() => setAiAssistantOpen(true)} color={aiAssistantOpen ? 'primary' : 'default'}>
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" sx={{ flex: 1 }}>{app.name || 'Untitled App'}</Typography>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Hint */}
      <Box sx={{ px: 2, py: 0.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Drag components from the palette or click to add. Click a panel to edit. <strong>Ctrl+S</strong> save, <strong>Ctrl+Enter</strong> run, <strong>Ctrl+Z</strong> undo.
        </Typography>
      </Box>

      {/* WYSIWYG: palette + canvas */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', bgcolor: previewSize !== 'desktop' ? 'grey.200' : 'transparent' }}>
        <DndProvider backend={HTML5Backend}>
          <AppBuilderComponentPalette onAddComponent={handlePaletteClickAdd} />
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: previewSize === 'phone' ? 375 : previewSize === 'tablet' ? 768 : '100%', flex: 1, minHeight: 0, overflow: 'hidden', boxShadow: previewSize !== 'desktop' ? 4 : 0, transition: 'max-width 0.3s' }}>
              <AppThemeProvider config={{ ...app, theme: form.theme || app?.theme }} darkMode={darkMode}>
                <DashboardProvider config={{ ...app, chatPosition: form.chatPosition || app?.chatPosition || 'center', theme: form.theme || app?.theme }}>
                  {(form.chatPosition || app?.chatPosition) === 'header' && (
                    <DashboardChatBar darkMode={darkMode} />
                  )}
                  <DashboardLayout
                    config={{ ...app, chatPosition: form.chatPosition || app?.chatPosition || 'center', layout: form.layout || app?.layout || 'three-panel', theme: form.theme || app?.theme }}
                    darkMode={darkMode}
                    editMode
                    onPanelClick={openPanelConfig}
                    onAddPanel={handleAddPanel}
                    onDropPanel={handleDropFromPalette}
                    selectedPanelId={panelForm?.panelId}
                  />
                </DashboardProvider>
              </AppThemeProvider>
            </Box>
          </Box>
        </DndProvider>
      </Box>

      <Drawer
        anchor="right"
        open={!!drawerContent}
        onClose={() => { setDrawerContent(null); setPanelForm(null); }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            height: '100%',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1300,
            boxShadow: (t) => t.shadows[8],
          },
        }}
      >
        <Box sx={{ p: 2, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }}>
            <Typography variant="h6">
              {drawerContent === 'settings' ? 'App settings' : 'Panel properties'}
            </Typography>
            <IconButton size="small" onClick={() => { setDrawerContent(null); setPanelForm(null); }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
          {drawerContent === 'settings' && (
            <>
              <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'auto', overflowY: 'scroll', pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Theme & look</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Pick a theme — colors are chosen for you
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                  {Object.entries(THEMES).map(([id, theme]) => {
                    const selected = (form.theme?.themeId || form.theme?.colorPreset || 'ocean') === id;
                    return (
                      <Box
                        key={id}
                        onClick={() => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), themeId: id } }))}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                          p: 1.5,
                          borderRadius: 1,
                          border: 2,
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'action.selected' : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            height: 32,
                            borderRadius: 1,
                            background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.accent} 100%)`,
                          }}
                        />
                        <Typography variant="caption" fontWeight={selected ? 600 : 400}>
                          {theme.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Light or dark</InputLabel>
                  <Select
                    value={form.theme?.mode ?? 'auto'}
                    label="Light or dark"
                    onChange={(e) => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), mode: e.target.value } }))}
                  >
                    <MenuItem value="auto">Auto (follow platform)</MenuItem>
                    <MenuItem value="light">Light</MenuItem>
                    <MenuItem value="dark">Dark</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Header style</InputLabel>
                  <Select
                    value={form.theme?.headerStyle || 'flat'}
                    label="Header style"
                    onChange={(e) => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), headerStyle: e.target.value } }))}
                  >
                    <MenuItem value="flat">Flat</MenuItem>
                    <MenuItem value="gradient">Gradient</MenuItem>
                    <MenuItem value="minimal">Minimal</MenuItem>
                  </Select>
                </FormControl>
                <Box>
                  <Typography variant="caption" color="text.secondary">Border radius</Typography>
                  <Slider
                    size="small"
                    value={form.theme?.borderRadius ?? 8}
                    onChange={(_, v) => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), borderRadius: v } }))}
                    min={0}
                    max={24}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <TextField
                  label="Font family (optional)"
                  size="small"
                  fullWidth
                  placeholder="e.g. Inter, system-ui"
                  value={form.theme?.fontFamily || ''}
                  onChange={(e) => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), fontFamily: e.target.value } }))}
                />
                <TextField
                  label="Logo URL"
                  size="small"
                  fullWidth
                  placeholder="https://..."
                  value={form.theme?.logoUrl || ''}
                  onChange={(e) => setForm((f) => ({ ...f, theme: { ...(f.theme || {}), logoUrl: e.target.value } }))}
                />

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">App basics</Typography>
                <TextField label="App name" fullWidth size="small" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <TextField label="Description" fullWidth size="small" multiline rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" />
                <FormControl fullWidth size="small">
                  <InputLabel>Default flow</InputLabel>
                  <Select value={form.defaultFlowId || ''} label="Default flow" onChange={(e) => setForm((f) => ({ ...f, defaultFlowId: e.target.value }))} disabled={flowsLoading}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {flows.map((f) => (<MenuItem key={f._id || f.id} value={f._id || f.id}>{f.name || 'Untitled flow'}</MenuItem>))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Layout</InputLabel>
                  <Select value={form.layout || 'three-panel'} label="Layout" onChange={(e) => setForm((f) => ({ ...f, layout: e.target.value }))}>
                    <MenuItem value="two-panel">Two Panel (left + right)</MenuItem>
                    <MenuItem value="three-panel">Three Panel</MenuItem>
                    <MenuItem value="dynamic">Dynamic</MenuItem>
                    <MenuItem value="grid">Grid (resizable, draggable)</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Chat position</InputLabel>
                  <Select value={form.chatPosition || 'center'} label="Chat position" onChange={(e) => setForm((f) => ({ ...f, chatPosition: e.target.value }))}>
                    <MenuItem value="center">Center panel (chat-focused)</MenuItem>
                    <MenuItem value="header">Header bar (center = session summary)</MenuItem>
                  </Select>
                </FormControl>

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Usage analytics (last 30 days)</Typography>
                {analytics ? (
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip label={`Views: ${analytics.views || 0}`} size="small" variant="outlined" />
                    <Chip label={`Runs: ${analytics.runs || 0}`} size="small" variant="outlined" />
                    <Chip label={`Flow OK: ${analytics.flowCompletes || 0}`} size="small" color="success" variant="outlined" />
                    <Chip label={`Flow errors: ${analytics.flowErrors || 0}`} size="small" color={analytics.flowErrors ? 'error' : 'default'} variant="outlined" />
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">Loading...</Typography>
                )}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Version history</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Restore a previous version. Versions are created on each save (last 20 kept).
                </Typography>
                {(app?.versions || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No previous versions yet. Save to create one.</Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {(app?.versions || []).slice(0, 5).map((v) => (
                      <Stack key={v.id} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption">{new Date(v.timestamp).toLocaleString()}</Typography>
                        <Button size="small" onClick={() => handleRestoreVersion(v)} disabled={saving}>Restore</Button>
                      </Stack>
                    ))}
                    {(app?.versions || []).length > 5 && (
                      <Typography variant="caption" color="text.secondary">… and {(app.versions.length - 5)} more</Typography>
                    )}
                  </Stack>
                )}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Sharing</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Share this app with teammates. Editors can modify; viewers can run only.
                </Typography>
                {(shares || []).map((s) => (
                  <Stack key={s.userId} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="body2">{s.userEmail || s.userName || s.userId}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Chip label={s.role} size="small" variant="outlined" />
                      <IconButton size="small" onClick={async () => {
                        const { error } = await removeAppShare(app._id || app.id, s.userId);
                        if (!error) listAppShares(app._id || app.id).then(({ data }) => setShares(data || []));
                      }} aria-label="Remove">
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}
                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search by email or username"
                    value={shareSearchQuery}
                    onChange={(e) => setShareSearchQuery(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Select size="small" value={shareRoleToAdd} onChange={(e) => setShareRoleToAdd(e.target.value)} sx={{ minWidth: 90 }}>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                </Stack>
                {shareSearchResults.length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {shareSearchResults.slice(0, 5).map((u) => (
                      <Stack key={u._id || u.id} direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption">{u.email || u.username}</Typography>
                        <Button
                          size="small"
                          disabled={addingShare || shares?.some((s) => String(s.userId) === String(u._id || u.id))}
                          onClick={async () => {
                            setAddingShare(true);
                            const { error } = await addAppShare(app._id || app.id, { targetUserId: u._id || u.id, role: shareRoleToAdd });
                            setAddingShare(false);
                            if (!error) {
                              listAppShares(app._id || app.id).then(({ data }) => setShares(data || []));
                              setShareSearchQuery('');
                              setShareSearchResults([]);
                            }
                          }}
                        >
                          Add
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Publish & embed</Typography>
                <Typography variant="caption" color="text.secondary">Public run-only URL (no edit UI)</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <TextField
                    size="small"
                    fullWidth
                    readOnly
                    value={app?._id || app?.id ? `${window.location.origin}/p/${app._id || app.id}` : ''}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  <IconButton size="small" onClick={() => app?._id && navigator.clipboard?.writeText(`${window.location.origin}/p/${app._id || app.id}`)} title="Copy URL">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" href={app?._id ? `/p/${app._id || app.id}` : '#'} target="_blank" rel="noopener noreferrer" title="Open in new tab">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>Embed (iframe)</Typography>
                <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    readOnly
                    value={app?._id || app?.id ? `<iframe src="${window.location.origin}/p/${app._id || app.id}" width="100%" height="600" frameborder="0"></iframe>` : ''}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 11 } }}
                  />
                  <IconButton size="small" onClick={() => app?._id && navigator.clipboard?.writeText(`<iframe src="${window.location.origin}/p/${app._id || app.id}" width="100%" height="600" frameborder="0"></iframe>`)} title="Copy embed code">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Button onClick={handleSaveApp} variant="contained" fullWidth disabled={saving}>
                {saving ? <CircularProgress size={20} /> : 'Save settings'}
              </Button>
            </>
          )}
          {drawerContent === 'panel' && panelForm && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {panelForm.panelPosition} — {panelForm.panelLabel}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2} sx={{ flex: 1, overflow: 'auto', pt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Component type</InputLabel>
                  <Select
                    value={panelForm.componentType || 'table'}
                    label="Component type"
                    onChange={(e) => {
                      const t = e.target.value;
                      setPanelForm((f) => ({ ...f, componentType: t, outputKey: getDefaultOutputKey(t), panelLabel: COMPONENT_REGISTRY[t]?.label || t }));
                    }}
                  >
                    {COMPONENT_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{COMPONENT_REGISTRY[t]?.label || t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {panelForm.componentType === 'chart' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Chart type</InputLabel>
                    <Select value={panelForm.chartType || 'bar'} label="Chart type" onChange={(e) => setPanelForm((f) => ({ ...f, chartType: e.target.value }))}>
                      <MenuItem value="bar">Bar</MenuItem>
                      <MenuItem value="line">Line</MenuItem>
                      <MenuItem value="pie">Pie</MenuItem>
                      <MenuItem value="area">Area</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth size="small">
                  <InputLabel>Output key</InputLabel>
                  <Select value={panelForm.outputKey || 'table_data'} label="Output key" onChange={(e) => setPanelForm((f) => ({ ...f, outputKey: e.target.value }))}>
                    {OUTPUT_KEYS.map((k) => (
                      <MenuItem key={k} value={k}>{k}</MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Flow must return this key</Typography>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Flow</InputLabel>
                  <Select value={panelForm.flowId || ''} label="Flow" onChange={(e) => setPanelForm((f) => ({ ...f, flowId: e.target.value }))} disabled={flowsLoading}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {flows.map((f) => (
                      <MenuItem key={f._id || f.id} value={f._id || f.id}>{f.name || 'Untitled flow'}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox checked={panelForm.refreshOnMount === true} onChange={(e) => setPanelForm((f) => ({ ...f, refreshOnMount: e.target.checked }))} />}
                  label="Refresh on load (run flow when app opens)"
                />
                <FormControlLabel
                  control={<Checkbox checked={panelForm.collapsible === true} onChange={(e) => setPanelForm((f) => ({ ...f, collapsible: e.target.checked }))} />}
                  label="Collapsible panel"
                />
                {panelForm.collapsible && (
                  <FormControlLabel
                    control={<Checkbox checked={panelForm.collapsedDefault === true} onChange={(e) => setPanelForm((f) => ({ ...f, collapsedDefault: e.target.checked }))} />}
                    label="Start collapsed"
                  />
                )}
                <TextField
                  label="Visibility condition"
                  size="small"
                  fullWidth
                  placeholder="e.g. selectedRow or !selectedRow"
                  value={panelForm.visibilityCondition || ''}
                  onChange={(e) => setPanelForm((f) => ({ ...f, visibilityCondition: e.target.value }))}
                  helperText="Show only when condition is true. Examples: selectedRow (when row selected), !selectedRow (when none)"
                />
                {panelForm.componentType === 'table' && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Drill-down (row click)</Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Drill-down flow</InputLabel>
                      <Select value={panelForm.drillDownFlowId || ''} label="Drill-down flow" onChange={(e) => setPanelForm((f) => ({ ...f, drillDownFlowId: e.target.value }))} disabled={flowsLoading}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {flows.map((f) => (<MenuItem key={f._id || f.id} value={f._id || f.id}>{f.name || 'Untitled flow'}</MenuItem>))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Target panel (show result)</InputLabel>
                      <Select value={panelForm.drillDownTargetPanelId || ''} label="Target panel" onChange={(e) => setPanelForm((f) => ({ ...f, drillDownTargetPanelId: e.target.value }))}>
                        <MenuItem value=""><em>None</em></MenuItem>
                        {(app?.panels || []).filter((x) => x.type !== 'chat').map((p) => (
                          <MenuItem key={p.id} value={p.id}>{COMPONENT_REGISTRY[p.componentType]?.label || p.componentType || p.id}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Button color="error" size="small" startIcon={<RemoveIcon />} onClick={() => { handleRemovePanel(panelForm.panelId); setDrawerContent(null); setPanelForm(null); }}>
                  Remove panel
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button onClick={() => { setDrawerContent(null); setPanelForm(null); }}>Cancel</Button>
                  <Button variant="contained" onClick={savePanelConfig}>Save</Button>
                </Stack>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>

      <AppBuilderAIAssistant
        open={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        onApply={handleAIAssistantApply}
        existingPanels={app?.panels || []}
      />

      <Dialog open={addPanelDialogOpen} onClose={() => setAddPanelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add panel</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Position</InputLabel>
            <Select value={addPanelForm.position} label="Position" onChange={(e) => setAddPanelForm((f) => ({ ...f, position: e.target.value }))}>
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="bottom">Bottom</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Component type</InputLabel>
            <Select
              value={addPanelForm.componentType}
              label="Component type"
              onChange={(e) => { const t = e.target.value; setAddPanelForm((f) => ({ ...f, componentType: t, outputKey: t === 'summary' ? null : getDefaultOutputKey(t) })); }}
            >
              <MenuItem value="summary">Session Summary</MenuItem>
              {COMPONENT_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{COMPONENT_REGISTRY[t]?.label || t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {addPanelForm.componentType === 'chart' && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Chart type</InputLabel>
              <Select value={addPanelForm.chartType || 'bar'} label="Chart type" onChange={(e) => setAddPanelForm((f) => ({ ...f, chartType: e.target.value }))}>
                <MenuItem value="bar">Bar</MenuItem>
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
                <MenuItem value="area">Area</MenuItem>
              </Select>
            </FormControl>
          )}
          {addPanelForm.componentType !== 'summary' && (
            <>
              <FormControl fullWidth margin="dense">
                <InputLabel>Output key</InputLabel>
                <Select value={addPanelForm.outputKey} label="Output key" onChange={(e) => setAddPanelForm((f) => ({ ...f, outputKey: e.target.value }))}>
                  {OUTPUT_KEYS.map((k) => (
                    <MenuItem key={k} value={k}>{k}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="dense">
                <InputLabel>Flow</InputLabel>
                <Select value={addPanelForm.flowId || ''} label="Flow" onChange={(e) => setAddPanelForm((f) => ({ ...f, flowId: e.target.value }))} disabled={flowsLoading}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {flows.map((f) => (
                    <MenuItem key={f._id || f.id} value={f._id || f.id}>{f.name || 'Untitled flow'}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox checked={addPanelForm.refreshOnMount === true} onChange={(e) => setAddPanelForm((f) => ({ ...f, refreshOnMount: e.target.checked }))} />}
                label="Refresh on load"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddPanelDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveAddPanel}>Add panel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
